import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Card, CardBody, Col, Container, Row, Table } from "reactstrap";
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Chip,
  Tooltip,
  Input,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl, 
  InputLabel,  
  Select,     
  MenuItem,
} from "@mui/material";
import { Icon } from "@progress/kendo-react-common";
import { DateRangePicker } from "@progress/kendo-react-dateinputs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"; // For accordion icon
import ErrorBoundary from "../../ErrorBoundry";
import { advanceSearchDashboard, gg1ReportDownload } from "../../_helper/services/advanceSearchv2";

// --- Highcharts Imports ---
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
// import highchartsGantt from "highcharts/modules/gantt";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsExportData from "highcharts/modules/export-data";
import HighchartsGantt from "highcharts/modules/gantt";

// Initialize the Gantt module
// highchartsGantt(Highcharts);
if (typeof Highcharts === "object") {
  HighchartsGantt(Highcharts);
//   HighchartsExporting(Highcharts);
//   HighchartsExportData(Highcharts);
}


const orderedGroups = {
  Department: [
    "Dept Admin",
    "Submitted",
    "Cert Officer",
    "Agency Director",
  ],
  BBMR: [
    "BBMR-Assign Analyst",
    "BBMR-Analyst Review",
    "BBMR-Supervisor Review",
    "BBMR-Director Review",
  ],
  HR: [
    "Classification",
    "Recruitment",
    "EMR",
    "Drug Testing",
    "Records",
    "Submitted For Final Post",
    "Final Post NPA",
  ],
};
const allOrderedStatuses = [
  ...orderedGroups.Department,
  ...orderedGroups.BBMR,
  ...orderedGroups.HR,
];

// --- Helper function to get the start of the next month ---
const getNextMonthStart = (year, month) => {
  if (month === 11) { // JS months are 0-11
    return Date.UTC(year + 1, 0, 1);
  }
  return Date.UTC(year, month + 1, 1);
};

// --- Helper function to define chart data ---
// --- Helper function to define chart data ---
const getChartOptions = (data, dateRange) => {
  // 1. Define the exact order for all groups based on the image
  const orderedGroups = {
    Department: [
      "Dept Admin",
      "Submitted",
      "Cert Officer",
      "Agency Director"
    ],
    BBMR: [
      "BBMR-Assign Analyst",
      "BBMR-Analyst Review",
      "BBMR-Supervisor Review",
      "BBMR-Director Review"
    ],
    HR: [
      "Classification",
      "Recruitment",
      "EMR",
      "Drug Testing",
      "Records",
      "Submitted For Final Post",
      "Final Post NPA"
    ],
  };

  // 2. Get all unique statuses that are *actually in the data*
  const allStatusesInData = new Set(data.map((d) => d.StatusName));

  // 3. Create the final category lists based on the fixed order
  const yCategoriesFlat = []; // For "Status Name" column
  const yGroupCategories = []; // For "Section" column

  // Iterate over the defined groups and their fixed order
  Object.entries(orderedGroups).forEach(([groupName, statusList]) => {
    let isFirstItemInGroup = true;
    statusList.forEach((statusName) => {
      // Only add a row if this status exists in the current data
      if (allStatusesInData.has(statusName)) {
        yCategoriesFlat.push(statusName);
        
        // Add the group label only for the first item *of this group*
        // that is present in the data
        if (isFirstItemInGroup) {
          yGroupCategories.push(groupName);
          isFirstItemInGroup = false;
        } else {
          yGroupCategories.push("");
        }
      }
    });
  });

  // 4. Group the monthly data by StatusName
  const dataByStatus = data.reduce((acc, item) => {
    if (!acc[item.StatusName]) {
      acc[item.StatusName] = [];
    }
    acc[item.StatusName].push(item.MonthYear);
    return acc;
  }, {});

  // 5. Transform the groups into contiguous blocks for the Gantt series
  const seriesData = [];
  for (const statusName in dataByStatus) {
    const yIndex = yCategoriesFlat.indexOf(statusName);
    if (yIndex === -1) continue; // Skip if status isn't in our ordered list

    // Sort the months
    const sortedMonths = dataByStatus[statusName]
      .map((my) => {
        const [year, month] = my.split("-").map(Number);
        return { year, month: month - 1 }; // JS month (0-11)
      })
      .sort((a, b) => a.year - b.year || a.month - b.month);

    // Find contiguous blocks
    let currentBlock = null;
    for (const { year, month } of sortedMonths) {
      if (currentBlock === null) {
        currentBlock = { startYear: year, startMonth: month, endYear: year, endMonth: month };
      } else {
        const [nextYear, nextMonth] =
          currentBlock.endMonth === 11
            ? [currentBlock.endYear + 1, 0]
            : [currentBlock.endYear, currentBlock.endMonth + 1];

        if (year === nextYear && month === nextMonth) {
          currentBlock.endYear = year;
          currentBlock.endMonth = month;
        } else {
          seriesData.push({
            name: statusName,
            start: Date.UTC(currentBlock.startYear, currentBlock.startMonth, 1),
            end: getNextMonthStart(currentBlock.endYear, currentBlock.endMonth),
            y: yIndex,
          });
          currentBlock = { startYear: year, startMonth: month, endYear: year, endMonth: month };
        }
      }
    }
    // Save the very last block
    if (currentBlock) {
      seriesData.push({
        name: statusName,
        start: Date.UTC(currentBlock.startYear, currentBlock.startMonth, 1),
        end: getNextMonthStart(currentBlock.endYear, currentBlock.endMonth),
        y: yIndex,
      });
    }
  }

  // 6. Add unique IDs to series data to prevent Highcharts errors
  const seriesDataWithIds = seriesData.map((item, index) => ({
    ...item,
    id: `${item.name.toLowerCase().replace(/\s+/g, '-')}-${index}`
  }));

  // 7. Return the final chart options
  return {
    chart: { 
      type: "gantt",
      height: 650
    },
    title: { text: "Status Workflow Timeline" },
    xAxis: {
      type: "datetime",
      labels: { format: "{value:%b %Y}" },
      tickInterval: 24 * 3600 * 1000 * 30.44, // Approx 1 month
      min: dateRange.start ? dateRange.start.getTime() : null,
      max: dateRange.end ? dateRange.end.getTime() : null,
    },
    yAxis: {
      title: { text: 'Status Name'},
      categories: yCategoriesFlat,
     //  reversed: false,
      // grid: {
      //   columns: [
      //     { title: { text: "Section" }, categories: yGroupCategories },
      //     { title: { text: "Status Name" } },
      //   ],
      // },
    },
    
    tooltip: {
      formatter: function () {
        return `<b>${this.point.name}</b><br/>${Highcharts.dateFormat(
          "%b %Y",
          this.point.start
        )} - ${Highcharts.dateFormat(
          "%b %Y",
          this.point.end - 86400000 // Subtract 1 day
        )}`;
      },
    },
    plotOptions: {
      gantt: {
        dataLabels: {
          enabled: false
        },
        borderWidth: 1,
      },
    },
    series: [
      {
        name: "Status Timeline",
        data: seriesDataWithIds,
      },
    ],
    credits: { enabled: false },
  };
};

const buildGanttOptions = (data) => {
  if (!data || data.length === 0) return {};

  // Helper to convert date strings to timestamps
  const toTime = (dateStr) => new Date(dateStr).getTime();

  // --- Categorize the data ---
  const departmentSection = data.filter(d =>
    ["Dept Admin", "Submitted", "Cert Officer", "Agency Director"].includes(d.StatusName)
  );

  const bbmrSection = data.filter(d =>
    d.StatusName.startsWith("BBMR")
  );

  const hrSection = data.filter(d =>
    [
      "Classification",
      "Recruitment",
      "EMR",
      "Drug Testing",
      "Records",
      "Submitted For Final Post",
      "Final Post NPA"
    ].includes(d.StatusName)
  );

  // --- Convert to Gantt series ---
  const makeSeries = (name, items, color) => ({
    name,
    data: items.map(item => ({
      id: item.StatusName,
      name: item.StatusName,
      start: toTime(item.StartDate),
      end: toTime(item.EndDate),
      y: 0,
      color
    }))
  });

  const series = [
    makeSeries("Department", departmentSection, "#1976d2"),
    makeSeries("BBMR", bbmrSection, "#9c27b0"),
    makeSeries("HR", hrSection, "#2e7d32"),
  ];

  return {
    chart: {
      height: 500,
      zoomType: "x"
    },
    title: {
      text: "Audit Workflow Timeline"
    },
    xAxis: {
      type: "datetime",
      title: { text: "Timeline" },
    },
    yAxis: {
      type: "category",
      categories: ["Department", "BBMR", "HR"],
      title: { text: "Section" },
    },
    tooltip: {
      pointFormat: `
        <b>{point.name}</b><br/>
        Start: {point.start:%m/%d/%Y}<br/>
        End: {point.end:%m/%d/%Y}
      `,
    },
    series,
    plotOptions: {
      series: {
        pointPadding: 0.2,
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          format: "{point.name}",
          style: { fontSize: "10px" },
        },
      },
    },
  };
};

// const getSummaryBarChartOptions = (data) => {
//   const sortedData = data
//     .slice()
//     .sort(
//       (a, b) =>
//         allOrderedStatuses.indexOf(a.StatusName) -
//         allOrderedStatuses.indexOf(b.StatusName)
//     );

//   const categories = sortedData.map((d) => d.StatusName);

//   return {
//     chart: { type: "column" },
//     title: { text: "Duration Summary by Status" },
//     xAxis: { categories: categories },
//     yAxis: { title: { text: "Days" } },
//     tooltip: { shared: true },
//     plotOptions: { column: { grouping: true, borderWidth: 0 } },
//     series: [
//       {
//         name: "Total Days",
//         data: sortedData.map((d) => parseFloat(d.TotalDays)),
//       },
//       {
//         name: "Average Days",
//         data: sortedData.map((d) => parseFloat(d.AverageDays)),
//       },
//       {
//         name: "Median Days",
//         data: sortedData.map((d) => parseFloat(d.MedianDays)),
//       },
//     ],
//     credits: { enabled: false },
//   };
// };

// ---  Monthly Stacked Bar Chart ---

const getSummaryBarChartOptions = (data, chartType = "column") => {
  const sortedData = data
    .slice()
    .sort(
      (a, b) =>
        allOrderedStatuses.indexOf(a.StatusName) -
        allOrderedStatuses.indexOf(b.StatusName)
    );

  const categories = sortedData.map((d) => d.StatusName);

  // --- Pie Chart Configuration ---
  if (chartType === "pie") {
    return {
      chart: { type: "pie" },
      title: { text: "Total Days Distribution by Status" },
      tooltip: {
        pointFormat:
          "{series.name}: <b>{point.percentage:.1f}%</b> ({point.y:,.0f} days)",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: "<b>{point.name}</b>: {point.percentage:.1f} %",
          },
          showInLegend: true,
        },
      },
      series: [
        {
          name: "Total Days",
          colorByPoint: true,
          data: sortedData.map((d) => ({
            name: d.StatusName,
            y: parseFloat(d.TotalDays) || 0, // Ensure valid number
          })),
        },
      ],
      credits: { enabled: false },
    };
  }

  // --- Column/Line Chart Configuration (Dual Axis) ---
  const isAllLines = chartType === "line";

  return {
    chart: {
      type: isAllLines ? "line" : "column", // Default type, series will override
      zoomType: "x",
    },
    title: { text: "Duration Summary by Status" },
    xAxis: { categories: categories, crosshair: true },
    yAxis: [
      {
        // --- Primary Y-Axis (Left) ---
        title: {
          text: "Total Days",
          style: { color: Highcharts.getOptions().colors[0] },
        },
        labels: {
          format: "{value:,.0f}",
          style: { color: Highcharts.getOptions().colors[0] },
        },
      },
      {
        // --- Secondary Y-Axis (Right) ---
        title: {
          text: "Average / Median Days",
          style: { color: Highcharts.getOptions().colors[2] },
        },
        labels: {
          format: "{value:,.1f}",
          style: { color: Highcharts.getOptions().colors[2] },
        },
        opposite: true,
      },
    ],
    tooltip: { shared: true },
    plotOptions: {
      column: { grouping: true, borderWidth: 0 },
      line: { marker: { enabled: true, radius: 3 } },
    },
    series: [
      {
        name: "Total Days",
        type: isAllLines ? "line" : "column",
        yAxis: 0, // Use primary axis
        data: sortedData.map((d) => parseFloat(d.TotalDays)),
        tooltip: {
          valueSuffix: " days",
        },
      },
      {
        name: "Average Days",
        type: "line",
        yAxis: 1, // Use secondary axis
        data: sortedData.map((d) => parseFloat(d.AverageDays)),
        color: Highcharts.getOptions().colors[2], // Use a different color
        tooltip: {
          valueSuffix: " days",
        },
      },
      {
        name: "Median Days",
        type: "line",
        yAxis: 1, // Use secondary axis
        data: sortedData.map((d) => parseFloat(d.MedianDays)),
        color: Highcharts.getOptions().colors[3], // Use another different color
        tooltip: {
          valueSuffix: " days",
        },
      },
    ],
    credits: { enabled: false },
  };
};

const getMonthlyBarChartOptions = (data, selectedMonth) => {
  if (!selectedMonth) return { title: { text: "Select a month to see summary" } };

  const filteredData = data.filter((d) => d.MonthYear === selectedMonth);
  const categories = ["Department", "BBMR", "HR"];
  
  // Initialize series data structure
  const seriesData = {};
  allOrderedStatuses.forEach(statusName => {
    seriesData[statusName] = {
      name: statusName,
      data: [0, 0, 0] // One index for each category
    };
  });

  // Populate series data
  filteredData.forEach(item => {
    let groupIndex = -1;
    if (orderedGroups.Department.includes(item.StatusName)) groupIndex = 0;
    else if (item.StatusName.startsWith("BBMR")) groupIndex = 1;
    else if (orderedGroups.HR.includes(item.StatusName)) groupIndex = 2;

    if (groupIndex !== -1 && seriesData[item.StatusName]) {
      seriesData[item.StatusName].data[groupIndex] = parseFloat(item.AvgDays);
    }
  });

  const finalSeries = Object.values(seriesData).filter(
    s => s.data.some(d => d > 0) // Only include statuses that have data
  );

  return {
    chart: { type: "column" },
    title: { text: `Monthly Summary by Section (${selectedMonth})` },
    xAxis: { categories: categories },
    yAxis: { title: { text: "Average Days" }, stacked: true },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '{series.name}: {point.y:,.1f} days',
    },
    plotOptions: { column: { stacking: "normal" } },
    series: finalSeries,
    credits: { enabled: false },
  };
};

// Bar Chart for Total Days ---
const getTotalDaysBarChartOptions = (data, sortOrder = "decreasing") => {
  let sortedData = []; 

  switch (sortOrder) {
    case "increasing":
      sortedData = data
        .slice()
        .sort(
          (a, b) =>
            (parseFloat(b.TotalDays) || 0) - (parseFloat(a.TotalDays) || 0)
        );
      break;
    case "logical":
      sortedData = data
        .slice()
        .sort(
          (a, b) =>
            
            allOrderedStatuses.indexOf(b.StatusName) - allOrderedStatuses.indexOf(a.StatusName)
        );
      break;
    case "decreasing":
    default:
      sortedData = data
        .slice()
        .sort(
          (a, b) =>
             (parseFloat(a.TotalDays) || 0) - (parseFloat(b.TotalDays) || 0) 
        );
      break;
  }

  const categories = sortedData.map((d) => d.StatusName);
  const seriesData = sortedData.map((d) => parseFloat(d.TotalDays) || 0);
  console.log(data, sortedData, "sortedData1221");
  console.log(categories, seriesData, "seriesData1221");

  return {
    chart: {
      type: "column",
      height: categories.length * 25 + 100, 
    },
    title: { text: "Total Days by Status" },

    xAxis: {
      categories: categories,
      title: { text: "Status Name" },
      reversed: true,
      gridLineWidth: 1,
    },

    yAxis: {
      min: 0,
      title: { text: "Total Days" },
      labels: { format: "{value:,.0f}" },
    },

    tooltip: {
      pointFormat: "<b>{point.y:,.0f} days</b>",
    },

    plotOptions: {
      bar: {
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          format: "{point.y:,.0f}", 
          style: {
            textOutline: "none",
            color: "#333333",
            fontSize: "10px",
          },
        },
      },
    },

    series: [
      {
        name: "Total Days",
        data: seriesData,
        colorByPoint: true, 
      },
    ],

    legend: { enabled: false },
    credits: { enabled: false },
  };
};

const formatAsLocalDateTimeString = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const H = String(date.getHours()).padStart(2, '0');
  const M = String(date.getMinutes()).padStart(2, '0');
  const S = String(date.getSeconds()).padStart(2, '0');
  
  return `${y}-${m}-${d}T${H}:${M}:${S}`;
};



const AuditReportGG1 = () => {
  const tableName = "fn_StatusTimeSummaryByDept";
  const chartApiName = "fn_StatusTimeSummaryByDept_Monthly";

  // --- Component State ---
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [isSearched, setIsSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [chartData, setChartData] = useState([]);


  // --- Department State ---
  const [departmentList, setDepartmentList] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // --- Nature of Action State ---
  const [natureOfActionList, setNatureOfActionList] = useState([]);
  const [selectedNatureCodes, setSelectedNatureCodes] = useState([]);
  const [natureCodeSearchTerm, setNatureCodeSearchTerm] = useState("");

  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");

  const [summaryChartType, setSummaryChartType] = useState("column");
  const [totalDaysSortOrder, setTotalDaysSortOrder] = useState("decreasing");

//   / --- Accordion State ---
  const [isFilterAccordionOpen, setIsFilterAccordionOpen] = useState(false);
  const [isTableAccordionOpen, setIsTableAccordionOpen] = useState(true);
  const [isGanttAccordionOpen, setIsGanttAccordionOpen] = useState(true);
  const [isSummaryBarAccordionOpen, setIsSummaryBarAccordionOpen] = useState(true);
  const [isMonthlyBarAccordionOpen, setIsMonthlyBarAccordionOpen] = useState(true);
  const [isTotalDaysBarAccordionOpen, setIsTotalDaysBarAccordionOpen] = useState(true);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Fetch Departments
        const deptPayload = {
          searchParams: "[]",
          pageNumber: 1,
          pageSize: 10000,
          sortData: "",
          additionalWhere: "",
          id: "LK_DPDV",
          tableName: "LK_DPDV",
          columns: "distinct Dept_Code, DepartmentName",
          orderBy: "DepartmentName",
          sortBy: "asc",
          location: "global",
        };
        const deptResponse = await advanceSearchDashboard(
          deptPayload,
          "LK_DPDV"
        );
        const filteredDepts =
          deptResponse?.results.filter(
            (d) => d.Dept_Code && d.DepartmentName
          ) || [];
        setDepartmentList(filteredDepts);

        // Fetch Nature of Action Codes
        const naturePayload = {
          searchParams: "[]",
          pageNumber: 1,
          pageSize: 10000,
          sortData: "",
          additionalWhere: "",
          id: "pmrc_nature",
          tableName: "pmrc_nature",
          columns: "NATURE_CODE, NATURE_TITLE",
          location: "global",
        };
        const natureResponse = await advanceSearchDashboard(
          naturePayload,
          "pmrc_nature"
        );
        setNatureOfActionList(natureResponse?.results || []);
      } catch (error) {
        console.error("Failed to fetch filter data:", error);
      }
    };
    fetchFilterData();
  }, []);

  const handleSearch = async () => {
    if (!dateRange.start || !dateRange.end) {
      alert("Please select a valid start and end date.");
      return;
    }
    setIsSearched(true);
    setIsLoading(true);
    setSummaryData([]);
    setChartData([]);

    const inclusiveEndDate = dateRange.end ? new Date(dateRange.end) : null;
    if (inclusiveEndDate) {
      inclusiveEndDate.setHours(23, 59, 59, 999);
    }

    const searchParamsList = [
      {
        columnName: "StartDate",
        value: formatAsLocalDateTimeString(dateRange.start),
        operator: "=",
        dataType: "DATETIME",
      },
      {
        columnName: "EndDate",
        value: formatAsLocalDateTimeString(inclusiveEndDate),
        operator: "=",
        dataType: "DATETIME",
      },
    ];
    if (selectedDepartment && selectedDepartment.Dept_Code) {
      searchParamsList.push({
        columnName: "DepartmentID",
        value: selectedDepartment.Dept_Code,
        operator: "=",
        dataType: "VARCHAR(50)",
      });
    }
    const natureCodesString = selectedNatureCodes.join(",");
    if (natureCodesString) {
      searchParamsList.push({
        columnName: "NatureCodes",
        value: natureCodesString,
        operator: "=",
        dataType: "VARCHAR(MAX)",
      });
    }
    const tablePayload = {
      tableName: tableName,
      searchParams: JSON.stringify(searchParamsList),
      columns: "*",
      pageNumber: 1,
      pageSize: 10000,
      sortData: "",
      additionalWhere: "",
      location: "global",
      id: tableName,
    };
    const chartPayload = {
      tableName: chartApiName,
      searchParams: JSON.stringify(searchParamsList),
      columns: "*",
      pageNumber: 1,
      pageSize: 10000,
      sortData: "",
      additionalWhere: "",
      location: "global",
      id: chartApiName,
    };
    try {
    //   const response = await advanceSearchDashboard(payload, tableName);
    //   setSummaryData(response?.results || []); // Adjusted to use response.results
    const [tableResponse, chartResponse] = await Promise.all([
        advanceSearchDashboard(tablePayload, tableName),
        advanceSearchDashboard(chartPayload, chartApiName)
      ]);
      
      setSummaryData(tableResponse?.results || []);
      setChartData(chartResponse?.results || []);

      const uniqueMonths = [
        ...new Set(chartResponse?.results.map((d) => d.MonthYear) || []),
      ].sort();
      setAvailableMonths(uniqueMonths);
      if (uniqueMonths.length > 0) {
        setSelectedMonth(uniqueMonths[0]); 
      }
    } catch (error) {
      console.error("Failed to fetch status time summary:", error);
      alert("An error occurred while fetching the report.");
    } finally {
      setIsLoading(false);
    }
  };

  const buildSearchParams = () => {
    const inclusiveEndDate = dateRange.end ? new Date(dateRange.end) : null;
    if (inclusiveEndDate) {
      inclusiveEndDate.setHours(23, 59, 59, 999);
    }

    const searchParamsList = [
      {
        columnName: "StartDate",
        value: formatAsLocalDateTimeString(dateRange.start),
        operator: "=",
        dataType: "DATETIME",
      },
      {
        columnName: "EndDate",
        value: formatAsLocalDateTimeString(inclusiveEndDate),
        operator: "=",
        dataType: "DATETIME",
      },
    ];
    if (selectedDepartment && selectedDepartment.Dept_Code) {
      searchParamsList.push({
        columnName: "DepartmentID",
        value: selectedDepartment.Dept_Code,
        operator: "=",
        dataType: "VARCHAR(50)",
      });
    }
    const natureCodesString = selectedNatureCodes.join(",");
    if (natureCodesString) {
      searchParamsList.push({
        columnName: "NatureCodes",
        value: natureCodesString,
        operator: "=",
        dataType: "VARCHAR(MAX)",
      });
    }
    return searchParamsList;
  };


  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // --- Function to format numbers to 2 decimal places ---
  const formatNumber = (num) => {
    const number = parseFloat(num);
    if (isNaN(number)) {
      return num; // Return original value if not a valid number
    }
    return number.toFixed(1);
  };

  const displayNatureCodes = natureOfActionList
    .filter(
      (nc) =>
        nc.NATURE_TITLE &&
        nc.NATURE_TITLE.toLowerCase().includes(natureCodeSearchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aSelected = selectedNatureCodes.includes(a.NATURE_CODE);
      const bSelected = selectedNatureCodes.includes(b.NATURE_CODE);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

  // --- List of headers that contain numeric data ---
  const numericHeaders = [
    "TotalDays",
    "AverageDays",
    "MedianDays",
    "TotalMinutes",
    "AverageMinutes",
    "MedianMinutes",
  ];

  const excludedHeaders = [
    "DepartmentID",
    "NatureCodes",
    "TotalMinutes",
    "AverageMinutes",
    "MedianMinutes",
    "StartDate",
    "EndDate"
  ];
  const tableHeaders =
    summaryData.length > 0
      ? Object.keys(summaryData[0]).filter(
        (header) => !excludedHeaders.includes(header)
      )
      : [];

  const buildHeaderContentString = () => {
    const filterDescriptions = [];

    if (dateRange.start && dateRange.end) {
      filterDescriptions.push(
        `Date Range: ${formatDate(dateRange.start)} - ${formatDate(
          dateRange.end
        )}`
      );
    }

    if (selectedDepartment && selectedDepartment.DepartmentName) {
      filterDescriptions.push(`Department: ${selectedDepartment.DepartmentName}`);
    }

    if (selectedNatureCodes.length > 0) {
      const selectedNames = natureOfActionList
        .filter((item) => selectedNatureCodes.includes(item.NATURE_CODE))
        .map((item) => item.NATURE_TITLE.trim());

      if (selectedNames.length > 0) {
        filterDescriptions.push(`Nature of Action: ${selectedNames.join(", ")}`);
      }
    }

    return filterDescriptions.join("  |  ");
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    const payload = {
      tableName: tableName,
      searchParams: JSON.stringify(buildSearchParams()),
      columns: `${tableHeaders.join(",")}`,
      pageNumber: 1,
      pageSize: 10000,
      sortData: "",
      additionalWhere: "",
      location: "global",
      id: tableName,
      fileExtension: "pdf",
      isOJA: false,
      header: "LeaveReport",
      headerContent: `<div style="margin-bottom: 10px;">${buildHeaderContentString()}</div>`,
    };

    try {
      const response = await gg1ReportDownload(payload);
      const filename = `report_${new Date().getTime()}.pdf`;
      const blob = await response.data;
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download report:", error);
      alert("An error occurred while downloading the report.");
    } finally {
      setIsDownloading(false);
    }
  };

  const ganttChartOptions = useMemo(() => {
    try {
      if (!chartData || chartData.length === 0) {
        return null;
      }
      return getChartOptions(chartData, dateRange);
    } catch (error) {
      console.error('Error generating Gantt chart options:', error);
      return null;
    }
  }, [chartData, dateRange]);

  const summaryBarChartOptions = useMemo(
    () => getSummaryBarChartOptions(summaryData, summaryChartType),
    [summaryData, summaryChartType]
  );
  
  const monthlyBarChartOptions = useMemo(
    () => getMonthlyBarChartOptions(chartData, selectedMonth),
    [chartData, selectedMonth]
  );

  const totalDaysBarChartOptions = useMemo(
    () => getTotalDaysBarChartOptions(summaryData, totalDaysSortOrder),
    [summaryData,dateRange,totalDaysSortOrder]
  );

  useEffect(() => {
    return () => Highcharts.charts.forEach((c) => c && c.destroy());
  }, []);

  return (
    <ErrorBoundary>
      <Fragment>
        <Container fluid={true}>
          <Row className="bg-primary rounded py-3 mb-3">
            <Typography variant="h5">
              <Icon style={{ fontSize: "25px", margin: "10px" }} name="clock" />
              Audit Report
            </Typography>
          </Row>
          <Row>
            <Col sm="12" className="page-content">
              <Card>
                <CardBody>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Report Parameters
                    </Typography>
                    <Box
                      sx={{
                        mb: 2,
                      }}
                    >
                      <Autocomplete
                        options={departmentList}
                        getOptionLabel={(option) => option.DepartmentName || ""}
                        value={selectedDepartment}
                        onChange={(event, newValue) =>
                          setSelectedDepartment(newValue)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.Dept_Code === value.Dept_Code
                        }
                        sx={{ width: 300 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Department (Optional)"
                            size="small"
                          />
                        )}
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <DateRangePicker
                          value={dateRange}
                          style={{ width: "300px" }}
                          onChange={(e) => setDateRange(e.value)}
                        />
                        <div style={{ width: "100%" }}>
                          {selectedNatureCodes.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                alignItems: "center",
                                paddingLeft: 3,
                              }}
                            >
                              {selectedNatureCodes.map((code) => {
                                const nature = natureOfActionList.find(
                                  (n) => n.NATURE_CODE === code
                                );
                                return (
                                  <Chip
                                    key={code}
                                    label={nature?.NATURE_TITLE?.trim() || code}
                                    size="small"
                                    color="primary"
                                    onDelete={() => {
                                      setSelectedNatureCodes((current) =>
                                        current.filter((c) => c !== code)
                                      );
                                    }}
                                  />
                                );
                              })}
                              {/* {selectedNatureCodes.length > 3 && (
                                <Typography variant="body2" color="text.secondary">
                                  +{selectedNatureCodes.length - 3} more
                                </Typography>
                              )} */}
                            </Box>
                          )}
                        </div>
                      </div>
                    </Box>
                    <Accordion
                      expanded={isAccordionOpen}
                      onChange={() => setIsAccordionOpen(!isAccordionOpen)}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight="medium">
                          Nature of Action Filter (Optional)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {natureOfActionList.length > 0 ? (
                          <Box>
                            <Input
                              placeholder="Search Nature of Action..."
                              fullWidth
                              value={natureCodeSearchTerm}
                              onChange={(e) =>
                                setNatureCodeSearchTerm(e.target.value)
                              }
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ maxHeight: "150px", overflowY: "auto" }}>
                              {displayNatureCodes.map((nc) => (
                                <Tooltip
                                  title={nc.NATURE_TITLE.trim()}
                                  key={nc.NATURE_CODE}
                                  arrow
                                >
                                  <Chip
                                    label={nc.NATURE_TITLE.trim()}
                                    onClick={() => {
                                      setSelectedNatureCodes((current) =>
                                        current.includes(nc.NATURE_CODE)
                                          ? current.filter(
                                              (code) => code !== nc.NATURE_CODE
                                            )
                                          : [...current, nc.NATURE_CODE]
                                      );
                                    }}
                                    color={
                                      selectedNatureCodes.includes(
                                        nc.NATURE_CODE
                                      )
                                        ? "primary"
                                        : "default"
                                    }
                                    sx={{ m: 0.5, cursor: "pointer" }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        ) : (
                          <CircularProgress size={24} />
                        )}
                      </AccordionDetails>
                    </Accordion>
                    <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-start",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={
                          !dateRange.start || !dateRange.end || isLoading
                        }
                      >
                        {isLoading ? "Generating..." : "Generate Report"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleDownload}
                        disabled={
                          summaryData.length === 0 || isLoading || isDownloading
                        }
                      >
                        {isDownloading ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Download PDF"
                        )}
                      </Button>
                    </Box>
                  </Box>

                  {/* --- Results Table --- */}
                  {isSearched && (
                    <Box sx={{ p: 2 }}>
                      {isLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            my: 4,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : summaryData.length > 0 ? (
                        <Fragment>
                          <Accordion
                            expanded={isTableAccordionOpen}
                            onChange={() =>
                              setIsTableAccordionOpen(!isTableAccordionOpen)
                            }
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6" fontWeight="bold">
                                Summary Table
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Table bordered responsive hover>
                                <thead>
                                  <tr>
                                    {tableHeaders.map((header) => (
                                      <th key={header}>{header}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {summaryData.map((row, index) => (
                                    <tr key={index}>
                                      {tableHeaders.map((header) => {
                                        const isNumeric =
                                          numericHeaders.includes(header);
                                        let cellValue = row[header];

                                        if (
                                          header === "StartDate" ||
                                          header === "EndDate"
                                        ) {
                                          cellValue = formatDate(cellValue);
                                        }

                                        if (isNumeric) {
                                          if (header === "TotalDays") {
                                            cellValue = (
                                              parseInt(cellValue) || 0
                                            ).toLocaleString();
                                          } else if (header === "MedianDays") {
                                            cellValue =
                                              parseInt(cellValue) || 0;
                                          } else {
                                            cellValue = formatNumber(cellValue);
                                          }
                                        }

                                        return (
                                          <td
                                            key={`${index}-${header}`}
                                            style={{
                                              textAlign: isNumeric
                                                ? "right"
                                                : "left",
                                            }}
                                          >
                                            {cellValue}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                          {/* --- Chart Representation --- */}
                          {/* ---  Gantt Chart --- */}
                          {/* <Accordion
                            expanded={isGanttAccordionOpen}
                            onChange={() =>
                              setIsGanttAccordionOpen(!isGanttAccordionOpen)
                            }
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6" fontWeight="bold">
                                Status Timeline (Gantt)
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {summaryData && summaryData.length > 0 && chartData && chartData.length > 0 && ganttChartOptions ? (
                                <div style={{ minHeight: '650px' }}>
                                  <HighchartsReact
                                    highcharts={Highcharts}
                                    constructorType={"ganttChart"}
                                    options={ganttChartOptions}
                                  />
                                </div>
                              ) : (
                                <Typography>No chart data available</Typography>
                              )}
                            </AccordionDetails>
                          </Accordion> */}

                          {/* ---  Total Days Horizontal Bar Chart --- */}
                          <Accordion
                            expanded={isTotalDaysBarAccordionOpen}
                            onChange={() =>
                              setIsTotalDaysBarAccordionOpen(
                                !isTotalDaysBarAccordionOpen
                              )
                            }
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6" fontWeight="bold">
                                Total Days by Status
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <FormControl
                                sx={{ m: 1, minWidth: 240, mb: 2 }}
                                size="small"
                              >
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                  value={totalDaysSortOrder}
                                  Indented
                                  label="Sort By"
                                  onChange={(e) =>
                                    setTotalDaysSortOrder(e.target.value)
                                  }
                                >
                                  Data
                                  <MenuItem value={"decreasing"}>
                                    Decreasing Order
                                  </MenuItem>
                                  <MenuItem value={"increasing"}>
                                    Increasing Order
                                  </MenuItem>
                                  <MenuItem value={"logical"}>
                                    Process Order
                                  </MenuItem>
                                </Select>
                              </FormControl>
                              <HighchartsReact
                                highcharts={Highcharts}
                                options={totalDaysBarChartOptions}
                                key={totalDaysSortOrder}
                              />
                            </AccordionDetails>
                          </Accordion>

                          {/* ---  Summary Bar Chart --- */}
                          <Accordion
                            expanded={isSummaryBarAccordionOpen}
                            onChange={() =>
                              setIsSummaryBarAccordionOpen(
                                !isSummaryBarAccordionOpen
                              )
                            }
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6" fontWeight="bold">
                                Duration Summary by Status
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {/* --- NEW CHART TYPE SELECTOR --- */}
                              <FormControl
                                sx={{ m: 1, minWidth: 240, mb: 2 }}
                                size="small"
                              >
                                <InputLabel>Chart Type</InputLabel>
                                <Select
                                  value={summaryChartType}
                                  label="Chart Type"
                                  onChange={(e) =>
                                    setSummaryChartType(e.target.value)
                                  }
                                >
                                  <MenuItem value={"column"}>
                                    Column / Line (Combo)
                                  </MenuItem>
                                  <MenuItem value={"line"}>All Lines</MenuItem>
                                  <MenuItem value={"pie"}>
                                    Pie (Total Days Only)
                                  </MenuItem>
                                </Select>
                              </FormControl>

                              <HighchartsReact
                                highcharts={Highcharts}
                                options={summaryBarChartOptions}
                                key={summaryChartType}
                              />
                            </AccordionDetails>
                          </Accordion>

                          {/* --- Monthly Bar Chart --- */}
                          <Accordion
                            expanded={isMonthlyBarAccordionOpen}
                            onChange={() =>
                              setIsMonthlyBarAccordionOpen(
                                !isMonthlyBarAccordionOpen
                              )
                            }
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="h6" fontWeight="bold">
                                Monthly Summary by Section
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <FormControl
                                sx={{ m: 1, minWidth: 200 }}
                                size="small"
                              >
                                <InputLabel>Select Month</InputLabel>
                                <Select
                                  value={selectedMonth}
                                  label="Select Month"
                                  onChange={(e) =>
                                    setSelectedMonth(e.target.value)
                                  }
                                >
                                  {availableMonths.map((month) => (
                                    <MenuItem key={month} value={month}>
                                      {month}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <HighchartsReact
                                highcharts={Highcharts}
                                options={monthlyBarChartOptions}
                              />
                            </AccordionDetails>
                          </Accordion>
                        </Fragment>
                      ) : (
                        <Typography sx={{ textAlign: "center", my: 4 }}>
                          No data found for the selected criteria.
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    </ErrorBoundary>
  );
};

export default AuditReportGG1;